import type { Tone } from '../StatusBadge';
import type {
    PurchaseOrderBillingStatus,
    PurchaseOrderFulfillmentStatus,
    PurchaseOrderListItem
} from '../../../services/purchaseOrders';
import { vendorInvoiceVariance, type VarianceSource } from './poCost';

// Two orthogonal vocabularies, one palette: green = done, grey = not started,
// amber = contested. Dispatch has none of its own - a row implies it succeeded.

export const PO_FULFILLMENT_STATUSES = ['not_shipped', 'shipped'] as const;
export const PO_BILLING_STATUSES = ['not_billed', 'billed'] as const;

const DONE: Tone = { bg: 'bg-emerald-600' };
const NOT_STARTED: Tone = { bg: 'bg-gray-500' };

// One map across both vocabularies - the values are globally unique, so a
// status cannot mean "done" in one column and "not started" in another.
const PO_STATUS_TONES: Record<string, Tone> = {
    shipped: DONE,
    billed: DONE,
    not_shipped: NOT_STARTED,
    not_billed: NOT_STARTED
};

export const PO_FULFILLMENT_TONES = PO_STATUS_TONES;
export const PO_BILLING_TONES = PO_STATUS_TONES;

export function poStatusLabel(status: string): string {
    const spaced = status.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export type PoStatusKind = 'fulfillment' | 'billing';

const STATUS_KINDS: Record<
    PoStatusKind,
    { known: readonly string[]; tones: Record<string, Tone> }
> = {
    fulfillment: { known: PO_FULFILLMENT_STATUSES, tones: PO_FULFILLMENT_TONES },
    billing: { known: PO_BILLING_STATUSES, tones: PO_BILLING_TONES }
};

// Amber, not gray: an unmapped value is a frontend that has fallen behind the
// enum, and it must look wrong rather than blend in as a normal state.
const UNKNOWN_TONE: Tone = { bg: 'bg-amber-600' };

export interface PoStatusDisplay {
    label: string;
    tones: Record<string, Tone>;
    known: boolean;
}

// Total over its input: an unknown value (null and undefined included, which a
// server-side enum change produces) renders as "Unknown" rather than throwing.
export function poStatusDisplay(
    kind: PoStatusKind,
    value: string | null | undefined
): PoStatusDisplay {
    const { known, tones } = STATUS_KINDS[kind];
    if (value != null && known.includes(value)) {
        return { label: poStatusLabel(value), tones, known: true };
    }
    const key = value ?? '';
    return {
        label: key.trim().length > 0 ? `Unknown (${key})` : 'Unknown',
        tones: { [key]: UNKNOWN_TONE },
        known: false
    };
}

export function poStatusOptions(statuses: readonly string[]): { value: string; label: string }[] {
    return statuses.map((value) => ({ value, label: poStatusLabel(value) }));
}

const SHIPPING_INCOMPLETE: readonly PurchaseOrderFulfillmentStatus[] = ['not_shipped'];
const MONEY_COMMITTED: readonly PurchaseOrderBillingStatus[] = ['billed'];

export type PoAttentionTone = 'warning' | 'danger';

export interface PoAttentionFlag {
    key: string;
    label: string;
    detail: string;
    tone: PoAttentionTone;
}

export type AttentionSource = VarianceSource &
    Pick<PurchaseOrderListItem, 'fulfillmentStatus' | 'billingStatus'>;

// Contradictions between the two statuses are the point of showing them
// separately - surface them instead of letting one badge paper over them.
export function poAttentionFlags(po: AttentionSource): PoAttentionFlag[] {
    const flags: PoAttentionFlag[] = [];

    if (
        MONEY_COMMITTED.includes(po.billingStatus) &&
        SHIPPING_INCOMPLETE.includes(po.fulfillmentStatus)
    ) {
        flags.push({
            key: 'billed_before_shipped',
            label: 'Billed before shipping',
            detail: `Billing is "${poStatusLabel(po.billingStatus)}" while shipping is still "${poStatusLabel(po.fulfillmentStatus)}".`,
            tone: 'warning'
        });
    }

    const variance = vendorInvoiceVariance(po);
    if (variance.kind === 'known' && !variance.withinTolerance) {
        flags.push({
            key: 'cost_variance',
            label: 'Invoice differs from expected',
            detail:
                variance.varianceCents > 0
                    ? 'The vendor billed more than the expected cost.'
                    : 'The vendor billed less than the expected cost.',
            tone: 'warning'
        });
    }

    return flags;
}
